import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { FileText, X } from "lucide-react";
import { sfx } from "@/lib/sound";

const STATUS_CLS = {
  Granted: "text-[#0284C7]",
  Registered: "text-[#0284C7]",
  "Under Prosecution": "text-[#D97706]",
  "Patent Pending": "text-[#D97706]",
  Advertised: "text-[#D97706]",
  "In Review": "text-[#D97706]",
  "In Preparation": "text-[#94A3B8]",
  Filed: "text-[#94A3B8]",
  Applied: "text-[#94A3B8]",
};

export default function VaultDetail({ record, onClose }) {
  const isTm = record.type === "tm";
  const item = record.item;
  const title = isTm ? item.mark : item.family;
  const sub = isTm ? `${item.type} · CLASS ${item.cls}` : item.kind;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" || e.key === "Backspace") {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  return (
    <motion.div
      className="fixed inset-0 z-40 flex items-center justify-center p-4 sm:p-8"
      data-testid="vault-detail"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.22 }}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-[#F1F5F9]/70 backdrop-blur-md"
        data-testid="vault-detail-backdrop"
      />
      <motion.div
        className="iv-glass iv-overlay-card relative w-full max-w-xl overflow-hidden rounded-2xl"
        initial={{ scale: 0.93, y: 22 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 14, opacity: 0 }}
        transition={{ type: "spring", stiffness: 280, damping: 26 }}
      >
        <div className="flex items-center justify-between gap-4 border-b border-white/70 px-6 py-4">
          <div className="flex items-center gap-3 min-w-0">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0284C7]/10">
              <FileText size={15} color="#0284C7" />
            </span>
            <div className="min-w-0">
              <div className="f-mono text-[8px] tracking-[0.3em] text-[#0284C7]">
                {isTm ? "TRADEMARK DOSSIER" : "PATENT DOSSIER"}
              </div>
              <div className="f-orbitron truncate text-sm font-bold text-[#0F172A]">
                {title}
                {isTm && <span className="ml-1 font-normal text-[#94A3B8]">™</span>}
              </div>
            </div>
          </div>
          <button
            type="button"
            data-testid="vault-detail-close"
            onClick={onClose}
            aria-label="Close dossier"
            className="iv-glass flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[#475569] transition-colors hover:text-[#0284C7]"
          >
            <X size={15} />
          </button>
        </div>

        <div className="px-6 py-5">
          <div className="flex flex-wrap gap-2">
            <span className="iv-chip !py-1.5 !text-[9px]">{sub}</span>
            <span className="iv-chip !py-1.5 !text-[9px]">{item.number}</span>
            <span className={`iv-chip !py-1.5 !text-[9px] ${STATUS_CLS[item.status]}`}>
              {item.status.toUpperCase()}
            </span>
            {item.validity && (
              <span className="iv-chip !py-1.5 !text-[9px] text-[#0284C7]">{item.validity.toUpperCase()}</span>
            )}
          </div>

          {isTm && (
            <p className="mt-3 f-mono text-[9px] tracking-[0.15em] text-[#64748B]">
              {item.goods.toUpperCase()}
            </p>
          )}

          <p className="mt-4 text-[13px] leading-relaxed text-[#475569]" data-testid="vault-detail-story">
            {item.story}
          </p>

          <div className="mt-6 f-mono text-[8px] tracking-[0.35em] text-[#0284C7]">TIMELINE</div>
          <div className="mt-3 space-y-0" data-testid="vault-detail-timeline">
            {item.timeline.map((ev, i) => (
              <motion.div
                key={`${ev.y}-${i}`}
                className="relative flex gap-4 pb-4 last:pb-0"
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + i * 0.09, duration: 0.3 }}
              >
                <div className="flex flex-col items-center">
                  <span
                    className={`mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full ${
                      i === item.timeline.length - 1
                        ? "bg-[#0284C7] shadow-[0_0_8px_rgba(2,132,199,0.6)]"
                        : "border-[1.5px] border-[#94A3B8] bg-white"
                    }`}
                  />
                  {i < item.timeline.length - 1 && <span className="mt-1 w-px flex-1 bg-[#CBD5E1]" />}
                </div>
                <div className="pb-1">
                  <div className="f-orbitron text-[10px] font-bold text-[#0F172A]">{ev.y.toUpperCase()}</div>
                  <div className="text-[12px] leading-snug text-[#64748B]">{ev.e}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="border-t border-white/70 px-6 py-3 f-mono text-[8px] tracking-[0.25em] text-[#94A3B8]">
          ○ BACK / ESC TO CLOSE
        </div>
      </motion.div>
    </motion.div>
  );
}
